import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import AuthCallback from "@/components/AuthCallback";
import AiAssistant from "@/components/AiAssistant";
import InstallSplash from "@/components/InstallSplash";
import TermsReconsentModal from "@/components/TermsReconsent";
import Home from "@/pages/Home";
import Browse from "@/pages/Browse";
import CityPage from "@/pages/CityPage";
import MetroPage from "@/pages/MetroPage";
import BusinessProfile from "@/pages/BusinessProfile";
import Postcards from "@/pages/Postcards";
import Plans from "@/pages/Plans";
import Mailer from "@/pages/Mailer";
import GoLanding from "@/pages/GoLanding";
import PostcardDetail from "@/pages/PostcardDetail";
import Dashboard from "@/pages/Dashboard";
import Admin from "@/pages/Admin";
import Favorites from "@/pages/Favorites";
import PaymentResult from "@/pages/PaymentResult";
import PayDrop from "@/pages/PayDrop";
import Redeem from "@/pages/Redeem";
import ROICalculator from "@/pages/ROICalculator";
import ZipPage from "@/pages/ZipPage";
import Coverage from "@/pages/Coverage";
import ReferPage from "@/pages/ReferPage";
import ThankYou from "@/pages/ThankYou";
import RealEstateLanding from "@/pages/RealEstateLanding";
import ServiceLanding from "@/pages/ServiceLanding";
import About from "@/pages/About";
import Careers from "@/pages/Careers";
import Terms from "@/pages/Terms";
import PartnerProfile, { PartnerGo } from "@/pages/PartnerProfile";
import Blog from "@/pages/Blog";
import Prospector from "@/pages/Prospector";
import Podcast from "@/pages/Podcast";
import CareerInterview from "@/pages/CareerInterview";
import PodcastEpisode from "@/pages/PodcastEpisode";
import BlogPost from "@/pages/BlogPost";
import SeoOverrides from "@/components/SeoOverrides";
import { Loader2 } from "lucide-react";

function Protected({ children, admin }) {
  const { user, loading, openAuth } = useAuth();
  useEffect(() => { if (!loading && !user) openAuth(); }, [loading, user]); // eslint-disable-line react-hooks/exhaustive-deps
  if (loading) return <div className="min-h-[60vh] grid place-items-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (!user) return <Navigate to="/" replace />;
  if (admin && user.role !== "admin" && !user.is_staff) return <Navigate to="/" replace />;
  return children;
}

import AcceptInvite from "@/pages/AcceptInvite";

function Shell() {
  const location = useLocation();
  if ((location.hash || "").includes("session_id=")) return <AuthCallback />;
  return (
    <div className="min-h-screen flex flex-col">
      <InstallSplash />
      <TermsReconsentModal />
      <Header />
      <main className="flex-1">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/browse" element={<Browse />} />
          <Route path="/city/:slug" element={<CityPage />} />
          <Route path="/metro/:slug" element={<MetroPage />} />
          <Route path="/business/:slug" element={<BusinessProfile />} />
          <Route path="/postcards" element={<Postcards />} />
          <Route path="/plans" element={<Plans />} />
          <Route path="/mailer" element={<Mailer />} />
          <Route path="/go/:slug" element={<GoLanding />} />
          <Route path="/coverage" element={<Coverage />} />
          <Route path="/about" element={<About />} />
          <Route path="/careers" element={<Careers />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/podcast" element={<Podcast />} />
          <Route path="/careers/interview/:token" element={<CareerInterview />} />
          <Route path="/podcast/:slug" element={<PodcastEpisode />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/refer" element={<ReferPage />} />
          <Route path="/pay-drop/:id" element={<PayDrop />} />
          <Route path="/thank-you" element={<ThankYou />} />
          <Route path="/buy-a-home" element={<RealEstateLanding intent="buy" />} />
          <Route path="/sell-your-home" element={<RealEstateLanding intent="sell" />} />
          <Route path="/hvac" element={<ServiceLanding vertical="hvac" />} />
          <Route path="/plumbers" element={<ServiceLanding vertical="plumber" />} />
          <Route path="/roofers" element={<ServiceLanding vertical="roofer" />} />
          <Route path="/electricians" element={<ServiceLanding vertical="electrician" />} />
          <Route path="/landscapers" element={<ServiceLanding vertical="landscaper" />} />
          <Route path="/painters" element={<ServiceLanding vertical="painter" />} />
          <Route path="/garage-door" element={<ServiceLanding vertical="garage_door" />} />
          <Route path="/pest-control" element={<ServiceLanding vertical="pest_control" />} />
          <Route path="/grow-my-business" element={<ServiceLanding vertical="business" />} />
          <Route path="/calculator" element={<ROICalculator />} />
          <Route path="/postcards/:id" element={<PostcardDetail />} />
          <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
          <Route path="/admin" element={<Protected admin><Admin /></Protected>} />
          <Route path="/prospector" element={<Protected><Prospector /></Protected>} />
          <Route path="/accept-invite" element={<AcceptInvite />} />
          <Route path="/favorites" element={<Protected><Favorites /></Protected>} />
          <Route path="/payment/success" element={<PaymentResult />} />
          <Route path="/payment/cancel" element={<PaymentResult cancel />} />
          <Route path="/r/:code" element={<Redeem />} />
          <Route path="/redeem/:code" element={<Redeem />} />
          <Route path="/us/:state/:city/:zip" element={<ZipPage />} />
          <Route path="/p/:code" element={<PartnerProfile />} />
          <Route path="/p/:code/:key" element={<PartnerGo />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/term-of-service" element={<Terms />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      <HashTermsRedirect />
      <Footer />
      <AiAssistant />
      <SeoOverrides />
    </div>
  );
}

function HashTermsRedirect() {
  useEffect(() => {
    const go = () => { if (typeof window !== "undefined" && window.location.hash === "#term-of-service" && window.location.pathname !== "/terms") window.location.replace("/terms"); };
    go();
    window.addEventListener("hashchange", go);
    return () => window.removeEventListener("hashchange", go);
  }, []);
  return null;
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Shell />
      </AuthProvider>
    </BrowserRouter>
  );
}
